// src/features/auth/authSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as authAPI from './authAPI';
import { saveAuthFromResponse } from '../../services/api';

export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async (credentials, { rejectWithValue }) => {
    try {
      const data = await authAPI.login(credentials);
      return data;  // should include { user, token }
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: null,
    token: null,
    status: 'idle',  // 'idle' | 'loading' | 'succeeded' | 'failed'
    error: null,
  },
  reducers: {
    // Clears a failed-login Alert when the user backs out of the MFA code step.
    // Without it the old error stays mounted for the rest of the page's life.
    resetAuthStatus(state) {
      state.status = 'idle';
      state.error = null;
    },
    logout(state) {
      state.user = null;
      state.token = null;
      localStorage.removeItem('token');
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('token_expiry');
      localStorage.removeItem('role');
      localStorage.removeItem('actual_role');
      localStorage.removeItem('permissions');
      localStorage.removeItem('workflow_actions');
      localStorage.removeItem('is_platform_admin');
      localStorage.removeItem('email');
      localStorage.removeItem('name');
      localStorage.removeItem('employee_code');
      localStorage.removeItem('gender');
      localStorage.removeItem('pr');
      localStorage.removeItem('avatar_url');
      localStorage.removeItem('auth_payload_version');
      localStorage.removeItem('platform_selected_tenant_key');
      localStorage.removeItem('platform_selected_tenant_id');
      localStorage.removeItem('platform_selected_tenant_name');
      localStorage.removeItem('platform_selected_tenant_org_code');
      localStorage.removeItem('auth_tenant_key');
      localStorage.removeItem('auth_tenant_id');
      localStorage.removeItem('auth_tenant_name');
      localStorage.removeItem('auth_tenant_org_code');
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        // A password-only success is NOT a session when the tenant requires a
        // second factor: the body carries a challenge, no tokens. Stay 'idle'
        // so the page shows the code step instead of a stale error, and do not
        // persist anything.
        if (action.payload?.mfa_required && !action.payload?.access_token) {
          state.status = 'idle';
          state.error = null;
          return;
        }
        state.status = 'succeeded';
        state.user = action.payload.user;
        state.token = action.payload.access_token || action.payload.token || null;
        saveAuthFromResponse(action.payload);
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Login failed';
      });
  },
});

export const { logout, resetAuthStatus } = authSlice.actions;
export default authSlice.reducer;
